"use client";
import { throttle } from "lodash";
import cn from "classnames";
import s from "./ContentsTable.module.scss";
import { useEffect, useState, useRef, useCallback, MouseEvent } from "react";
import { animateElementScroll, animateScroll } from "../helpers/animateScroll";
import st from "@/components/pages/blog/ai-article/common.module.scss";

export type ContentsTableItem = {
  id: string;
  heading: string;
  level?: number;
};

export type ContentsTableProps = {
  items: ContentsTableItem[];
  shrinkHeight?: boolean;
  type?: "default" | "article";
};

type TocItem = {
  id: string;
  heading: string;
  level: number;
};

type TocItemNumbered = TocItem & { number: string };

const enumerateListItems = (items: TocItem[]): TocItemNumbered[] => {
  const counters: Record<TocItem["level"], number> = { 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

  const resetDeeper = (level: TocItem["level"]) => {
    for (let l = (level + 1) as TocItem["level"]; l <= 6; l = (l + 1) as TocItem["level"]) {
      counters[l] = 0;
    }
  };

  return items.map((item) => {
    const level = item.level;

    counters[level] += 1;
    resetDeeper(level);

    const parts: string[] = [];
    for (let l = 2 as TocItem["level"]; l <= level; l = (l + 1) as TocItem["level"]) {
      // If a deeper level appears without its parents, stop building
      if (l !== 2 && counters[l] === 0) break;
      parts.push(String(counters[l]));
    }

    const base = parts.join(".");
    const number = level === 2 ? `${base}.` : base;

    return { ...item, number };
  });
};

export const ContentsTable = ({ items, shrinkHeight = true, type = "default" }: ContentsTableProps) => {
  if (!items.length) return null;
  return <Core items={items} shrinkHeight={shrinkHeight} type={type} />;
};

const Core = ({ items, shrinkHeight, type }: ContentsTableProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [winWidth, setWinWidth] = useState(0);
  const [scrollingMode, setScrollingMode] = useState(true);

  const [isCollapsed, setCollapsed] = useState(false);
  const [isHovering, setHovering] = useState(false);

  const scroller = useRef<HTMLSpanElement>(null);
  const navContainer = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const scrollingModeRef = useRef<boolean>(scrollingMode);
  const activeIndexRef = useRef<number>(activeIndex);

  scrollingModeRef.current = scrollingMode;
  activeIndexRef.current = activeIndex;

  const checkPosition = useCallback(() => {
    let elemFound = false;
    const winTop = window.scrollY;
    const isMd = window.matchMedia("(max-width: 992px)").matches;

    if (!isMd && type === "article" && wrapperRef.current && !isHovering) {
      const elementTop = parseInt(getComputedStyle(wrapperRef.current).getPropertyValue("top"));
      const screenTop = wrapperRef.current.getBoundingClientRect().top;

      setCollapsed(screenTop < 0 || screenTop === elementTop);
    }

    if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
      setActiveIndex(items.length - 1);
      return;
    }

    items.forEach((item, index) => {
      if (elemFound) return;
      const targetElem = document.getElementById(item.id);
      if (!targetElem) return;

      const elTop = winTop + targetElem.getBoundingClientRect().top;

      if (window.matchMedia("(max-width: 992px)").matches) {
        const targetHeight = winTop + 200;

        if (targetHeight >= elTop && winTop < elTop + targetElem.clientHeight - 200) {
          setActiveIndex(index);

          elemFound = true;
        }
      } else {
        const targetHeight = winTop + 200;
        const topDiff = 0;

        const bottomDiff = 0;

        if (targetHeight >= elTop - topDiff && winTop < elTop + targetElem.clientHeight - bottomDiff) {
          setActiveIndex(index);

          elemFound = true;
        }
      }
    });
  }, [items, type, isHovering]);

  // const throttledCheckPosition = throttle(checkPosition, 100);
  const throttledCheckPosition = checkPosition;

  const checkTableScroll = useCallback(() => {
    if (!scrollingModeRef.current || !navContainer.current || !scroller.current) return;
    const targetElem = navContainer.current.querySelector(`[href="#${items[activeIndexRef.current]?.id}"]`);
    if (!targetElem) return;
    const parent = targetElem.parentElement;

    if (!parent) return;

    if (window.matchMedia("(max-width: 992px)").matches) {
      animateElementScroll(
        "x",
        navContainer.current,
        navContainer.current.scrollLeft +
          parent.getBoundingClientRect().left -
          30 -
          (navContainer.current.clientWidth / 2 - parent.clientWidth / 2 - 30),
        () => {
          if (!navContainer.current || !scroller.current) return;
          const leftPosition = navContainer.current.scrollLeft + parent.getBoundingClientRect().left;
          scroller.current.style.width = parent.clientWidth + "px";
          scroller.current.style.transform = `translateX(${leftPosition}px)`;
        }
      );
    } else {
      scroller.current.style.width = "4px";

      animateElementScroll(
        "y",
        navContainer.current,
        navContainer.current.scrollTop +
          (parent.getBoundingClientRect().top - navContainer.current.getBoundingClientRect().top) -
          navContainer.current.clientHeight / 2,
        () => {
          if (!navContainer.current || !scroller.current) return;
          const topPosition =
            navContainer.current.scrollTop +
            parent.getBoundingClientRect().top -
            navContainer.current.getBoundingClientRect().top +
            5;
          scroller.current.style.transform = `translateY(${topPosition}px)`;
        }
      );
    }
  }, [items]);

  useEffect(() => {
    checkTableScroll();
  }, [activeIndex, winWidth, checkTableScroll]);

  const updateWindowWidth = throttle(() => {
    setWinWidth(window.innerWidth);
  }, 200);

  useEffect(() => {
    window.addEventListener("scroll", throttledCheckPosition);
    window.addEventListener("resize", throttledCheckPosition);
    window.addEventListener("resize", updateWindowWidth);
    checkPosition();

    return () => {
      window.removeEventListener("scroll", throttledCheckPosition);
      window.removeEventListener("resize", throttledCheckPosition);
      window.removeEventListener("resize", updateWindowWidth);
    };
  }, [checkPosition, throttledCheckPosition, updateWindowWidth]);

  const onItemClick = (id: string, event?: MouseEvent) => {
    event?.preventDefault();

    const el = document.getElementById(id);

    setCollapsed(true);

    if (!el || !headingRef.current) return;

    const offset = 0;
    setScrollingMode(false);
    animateScroll(el, offset, () => {
      setScrollingMode(true);
      setTimeout(() => {
        checkTableScroll();
      }, 50);
    });
  };

  useEffect(() => {
    const hash = (location.hash || "").replace(/#/, "");

    if (!hash) return;

    const targetItem = items.find(({ id }) => id === hash);

    if (!targetItem) return;

    setTimeout(() => {
      const el = document.getElementById(hash);

      if (!el) return;

      const header = document.querySelector(".site-header");

      const offset = header ? -header.clientHeight : 0;

      const position = el.getBoundingClientRect().top + window.scrollY + offset;

      window.scrollTo(0, position);
    }, 0);
  }, [items]);

  const handleMouseOver = () => {
    setCollapsed(false);
    setHovering(true);
  };

  const handleMouseLeave = () => {
    setHovering(false);
  };

  const enumeratedItems = enumerateListItems(items.map((item) => ({ ...item, level: item.level || 2 })));

  return (
    <div
      className={cn(
        "site-contents-table",
        {
          [st.cardGlassHover]: type !== "article",
        },
        s.contentsTable,
        {
          [s.shrinkHeight]: shrinkHeight,
          [s.contentsTableArticle]: type === "article",
          "site-contents-table-article": type === "article",
          [s.isClosed]: isCollapsed,
          // [s.isClosed]: false,
        }
      )}
      ref={wrapperRef}
      onMouseLeave={handleMouseLeave}
    >
      <p className={cn(st.fSmallCaps, s.heading)} onMouseOver={handleMouseOver} ref={headingRef}>
        Table of contents
      </p>
      <div className={s.navWrapper}>
        <nav className={s.nav} ref={navContainer}>
          <span ref={scroller} className={s.navScroller}></span>
          <ul className={s.list}>
            {enumeratedItems.map((item, index) => (
              <li
                key={item.id}
                className={cn(s.item, {
                  [s.itemActive]: index === activeIndex,
                })}
              >
                <a
                  href={`#${item.id}`}
                  onClick={(event) => onItemClick(item.id, event)}
                  data-number={type === "article" ? item.number : undefined}
                  data-level={type === "article" ? item.level : undefined}
                >
                  {item.heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};
